import { useState } from "react";
import Modal from "shared/Modal";
import Button from "shared/Button";
import { submitLeaveRequest, adminCreateLeaveRequest, LeaveType, LEAVE_TYPE_OPTIONS } from "../api/leaveApi";
import { AttendanceEmployee } from "../api/attendanceApi";
import EmployeeSelect from "./EmployeeSelect";
import EmployeeLoadErrorBanner from "./EmployeeLoadErrorBanner";
import { useEmployeeList } from "./useEmployeeList";

const inputStyles =
  "w-full px-3 py-2 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all disabled:opacity-60";

interface Props {
  writeMode: "self-request" | "admin-direct";
  // admin-direct only — threaded down from AttendancePage's own employees
  // state (loaded for its filter) via LeaveRequestsPanel, so this doesn't
  // fetch its own copy every time "Add Leave" opens. Falls back to fetching
  // its own copy when absent (same reuse pattern as AttendanceActionModal).
  employees?: AttendanceEmployee[];
  onClose: () => void;
  onSuccess: () => void;
}

export default function LeaveRequestModal({ writeMode, employees: employeesProp, onClose, onSuccess }: Props) {
  const isAdminDirect = writeMode === "admin-direct";

  const [employeeCode, setEmployeeCode] = useState("");
  const [leaveType, setLeaveType] = useState<LeaveType>("annual");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const { employees, loadError: employeesLoadError, retry: loadEmployees } = useEmployeeList(isAdminDirect, employeesProp);

  const handleSubmit = async () => {
    setError("");
    if (isAdminDirect && !employeeCode) return setError("Select an employee.");
    if (!startDate || !endDate) return setError("Start and end date are required.");
    if (endDate < startDate) return setError("End date must not be before start date.");
    if (!reason.trim()) return setError("A reason is required.");

    setSubmitting(true);
    try {
      if (isAdminDirect) {
        await adminCreateLeaveRequest({
          employee_code: employeeCode, leave_type: leaveType, start_date: startDate, end_date: endDate,
          reason: reason.trim(), note: note.trim() || undefined,
        });
      } else {
        await submitLeaveRequest({ leave_type: leaveType, start_date: startDate, end_date: endDate, reason: reason.trim() });
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Something went wrong.");
    }
    setSubmitting(false);
  };

  return (
    <Modal size="sm" onClick={submitting ? undefined : onClose} closeButtonCLick={submitting ? undefined : onClose} modalClassName="p-6">
      <h2 className="text-lg font-semibold text-foreground font-inter mb-4">{isAdminDirect ? "Add Leave" : "Request Leave"}</h2>

      <div className="space-y-3">
        {isAdminDirect && (
          <div>
            <label className="text-xs text-muted-foreground font-inter">Employee</label>
            {employeesLoadError ? (
              <EmployeeLoadErrorBanner onRetry={loadEmployees} />
            ) : (
              <EmployeeSelect employees={employees} value={employeeCode} onChange={setEmployeeCode} className="mt-1" />
            )}
          </div>
        )}

        <div>
          <label className="text-xs text-muted-foreground font-inter">Leave Type</label>
          <select value={leaveType} onChange={(e) => setLeaveType(e.target.value as LeaveType)} className={`${inputStyles} mt-1`}>
            {LEAVE_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground font-inter">Start Date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={`${inputStyles} mt-1`} />
          </div>
          <div className="flex-1">
            <label className="text-xs text-muted-foreground font-inter">End Date</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={`${inputStyles} mt-1`} />
          </div>
        </div>

        <div>
          <label className="text-xs text-muted-foreground font-inter">Reason</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} className={`${inputStyles} mt-1 resize-none`} />
        </div>

        {isAdminDirect && (
          <div>
            <label className="text-xs text-muted-foreground font-inter">Note (optional)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className={`${inputStyles} mt-1 resize-none`} />
          </div>
        )}

        {error && <p className="text-sm text-destructive font-inter">{error}</p>}
      </div>

      <div className="flex items-center justify-end gap-3 mt-6">
        <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button type="button" variant="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Submitting…" : isAdminDirect ? "Add Leave" : "Submit Request"}
        </Button>
      </div>
    </Modal>
  );
}
